import { memo } from "react";
import { Box, Container, Typography, Button, Grid2 } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FullPaddingWrapper } from "../../Components/FullPaddingWrapper/FullPaddingWrapper";
import { RootState } from "../../Store/store";
import { setToggleAuthModal } from "../../Redux/authReducer";

// Assets
import heroImage from "./Assets/hero-why-choose-us.webp";
import laptopImage from "./Assets/Layer 1.webp";
import hammeringImage from "./Assets/hammering-nail.webp";
import smartphoneImage from "./Assets/finger-typing-smartphone-close-up.webp";
import coupleImage from "./Assets/couple-enjoying-lunch-with-sandwiches-home.webp";
import girlImage from "./Assets/young-beautiful-girl-posing-street-dress-with-backpack.webp";
import ctaImage from "./Assets/cta-2.webp";

interface FeatureSectionProps {
  image: string;
  title: string;
  description: string;
  imagePosition: "left" | "right";
  imageAlt: string;
}

const FeatureSection = memo(
  ({ image, title, description, imagePosition, imageAlt }: FeatureSectionProps) => {
    const isImageLeft = imagePosition === "left";

    return (
      <Grid2
        container
        spacing={{ xs: 4, md: 6 }}
        sx={{
          py: { xs: 4, md: 6, lg: 12 },
          flexDirection: {
            xs: "column",
            md: isImageLeft ? "row" : "row-reverse",
          },
          alignItems: "center",
        }}
      >
        {/* Image */}
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: isImageLeft ? "flex-start" : "flex-end",
              px: { xs: 0, md: 2 },
            }}
          >
            <Box
              component="img"
              src={image}
              alt={imageAlt}
              sx={{
                width: "100%",
                maxWidth: { xs: "100%" },
                height: "auto",
                borderRadius: "16px",
                objectFit: "cover",
              }}
            />
          </Box>
        </Grid2>

        {/* Text Content */}
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Box
            sx={{
              px: { xs: 0, md: 2 },
              textAlign: { xs: "left", md: "left" },
            }}
          >
            <Typography
              variant="h4"
              sx={{
                color: "primary.main",
                fontWeight: 700,
                fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
                lineHeight: 1.3,
                mb: 2,
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#000000",
                fontSize: { xs: "1rem", md: "1.1rem", lg: "1.3rem" },
                lineHeight: 1.5,
                maxWidth: 400,
                fontWeight: 500,
              }}
            >
              {description}
            </Typography>
          </Box>
        </Grid2>
      </Grid2>
    );
  }
);

const WhySimplerHome = memo(() => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector(
    (state: RootState) => state?.auth || {}
  );

  const isLoggedIn =
    isAuthenticated &&
    user &&
    Object.keys(user || {}).length > 0;

  const handleSignUpClick = () => {
    if (isLoggedIn) {
      navigate("/");
    } else {
      dispatch(setToggleAuthModal(true));
    }
  };

  const features = [
    {
      image: laptopImage,
      title: "All-in-One Home Hub",
      description:
        "Track maintenance, store documents, and manage vendors—all from your phone or desktop.",
      imagePosition: "left" as const,
      imageAlt: "Laptop showing SimplerHome dashboard",
    },
    {
      image: hammeringImage,
      title: "Get Rebates",
      description:
        "Get money back from things you even already do to your home.",
      imagePosition: "right" as const,
      imageAlt: "Person hammering a nail",
    },
    {
      image: smartphoneImage,
      title: "Smart Reminders",
      description:
        "Get ahead of seasonal upkeep and avoid costly surprises with automated alerts.",
      imagePosition: "left" as const,
      imageAlt: "Person using smartphone app",
    },
    {
      image: coupleImage,
      title: "Boost Your Home's Value",
      description:
        "Build a living history of upgrades and repairs that adds trust and resale appeal.",
      imagePosition: "right" as const,
      imageAlt: "Couple enjoying their home",
    },
    {
      image: girlImage,
      title: "Built for Homeowners",
      description:
        "SimplerHome makes life easier—and helps you work better with contractors, insurers, and realtors.",
      imagePosition: "left" as const,
      imageAlt: "Happy homeowner",
    },
  ];

  return (
    <Container maxWidth="xl" disableGutters>
      <FullPaddingWrapper>
        {/* Hero Banner */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            minHeight: { xs: 350, sm: 400, md: 500 },
            backgroundImage: `url(${heroImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            borderRadius: { xs: "16px", md: "24px" },
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
          }}
        >
          {/* Teal overlay */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "#004B4C",
              opacity: 0,
              mixBlendMode: "multiply",
            }}
          />

          {/* Hero Content */}
          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              p: {
                xl: "60px 80px",
                lg: "60px 60px",
                md: "50px 50px",
                sm: "40px 30px",
                xs: "30px 24px",
              },
              maxWidth: { xs: "100%", sm: "80%", md: 800 },
            }}
          >
            <Typography
              variant="h3"
              sx={{
                color: "#ffffff",
                fontWeight: 700,
                fontSize: { xs: "1.75rem", sm: "2.25rem", md: "2.75rem" },
                lineHeight: 1.2,
                mb: 2.5,
              }}
            >
              Effortless Living Starts Here
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: "#ffffff",
                fontSize: { xs: "0.95rem", md: "1.1rem" },
                lineHeight: 1.4,
                fontWeight: 300,
                mb: 3,
                opacity: 0.95,
                maxWidth: { xs: "100%", sm: "90%", md: 450 },
              }}
            >
              SimplerHome keeps everything about your property in one smart,
              secure place. Get money back from rebates. From repairs to
              renovations, we help you stay organized, proactive, and
              stress-free.
            </Typography>

            <Button
              variant="contained"
              color="secondary"
              size="large"
              onClick={handleSignUpClick}
              sx={{
                mt: 4,
                px: { xs: 5, md: 10, lg: 15 },
                py: { xs: 1.5, md: 2, lg: 2.5 },
                fontSize: { xs: "1rem", md: "1.1rem", lg: "1.2rem" },
                fontWeight: 500,
                borderRadius: "8px",
                textTransform: "none",
                color: "#ffffff",
              }}
            >
              Sign Up Now!
            </Button>
          </Box>
        </Box>

        {/* Features Section */}
        <Box
          sx={{
            pt: { xs: 4, md: 8 },
            pb: { xs: 4, md: 8 },
            px: { xs: 2, lg: 6, xl: 10 },
          }}
        >
          {features.map((feature, index) => (
            <FeatureSection
              key={index}
              image={feature.image}
              title={feature.title}
              description={feature.description}
              imagePosition={feature.imagePosition}
              imageAlt={feature.imageAlt}
            />
          ))}
        </Box>

        {/* Bottom CTA Section */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            minHeight: { xs: 280, sm: 320, md: 380 },
            backgroundImage: `url(${ctaImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            borderRadius: { xs: "16px", md: "24px" },
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: { xs: 4, md: 6 },
          }}
        >
          {/* Teal overlay */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "#004B4C",
              opacity: 0,
              mixBlendMode: "multiply",
            }}
          />

          {/* CTA Content */}
          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              textAlign: "center",
              p: { xs: 3, md: 5 },
            //   maxWidth: 600,
            }}
          >
            <Typography
              variant="h4"
              sx={{
                color: "#ffffff",
                fontWeight: 600,
                fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2.25rem", lg: "2.5rem" },
                lineHeight: 1.3,
                mb: 2,
              }}
            >
              Smarter home ownership starts here.
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: "#ffffff",
                fontSize: { xs: "0.95rem", md: "1rem", lg: "1.2rem" },
                lineHeight: 1.5,
                fontWeight: 300,
                mb: 4,
                opacity: 0.9,
                maxWidth: 400,
                mx: "auto"
              }}
            >
              Join the movement toward a simpler, more powerful way to manage
              your home.
            </Typography>

            <Button
              variant="contained"
              color="secondary"
              size="large"
              onClick={handleSignUpClick}
              sx={{
                px: { xs: 5, md: 10, lg: 15 },
                py: { xs: 1.5, md: 2, lg: 2.5 },
                fontSize: { xs: "1rem", md: "1.1rem", lg: "1.2rem" },
                fontWeight: 500,
                borderRadius: "8px",
                textTransform: "none",
                color: "#ffffff",
              }}
            >
              Sign Up Now!
            </Button>
          </Box>
        </Box>
      </FullPaddingWrapper>
    </Container>
  );
});

export default WhySimplerHome;
